import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, projectsAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Briefcase, AlertTriangle, Clock, ArrowUp, ArrowRight, ChevronDown, ChevronUp, Loader2, Users } from 'lucide-react';
import JobCompletionDialog from '@/components/JobCompletionDialog';
import { formatDate } from '@/utils/dateFormat';

const PRIORITY_CONFIG = {
  high: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-l-red-500', icon: ArrowUp },
  normal: { color: 'text-foreground-muted', bg: '', border: '', icon: ArrowRight },
  low: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: '', icon: null },
};

const MyJobsSection = () => {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedJob, setCompletedJob] = useState(null);
  const [showTeam, setShowTeam] = useState(false);

  const isManager = ['owner', 'admin', 'manager'].includes(user?.role);

  useEffect(() => { fetchTasks(); }, []);

  const fetchTasks = async () => {
    try {
      const res = await dashboardAPI.myTasks(7);
      setData(res.data);
    } catch {
      // silently fail
    } finally { setLoading(false); }
  };

  const handleStatusChange = async (job, newStatus) => {
    try {
      const payload = { status: newStatus };
      if (newStatus === 'done') payload.completed = true;
      await projectsAPI.updateJob(job.project_id, job.job_id, payload);
      if (newStatus === 'done') {
        setCompletedJob(job);
      }
      fetchTasks();
    } catch { /* ignore */ }
  };

  if (loading) return (
    <section data-testid="my-jobs-loading">
      <div className="flex items-center gap-2 mb-4">
        <Briefcase size={16} className="text-accent" />
        <h2 className="text-xs uppercase tracking-wide text-foreground-muted">{t('dashboard.my_jobs')}</h2>
      </div>
      <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-foreground-muted" /></div>
    </section>
  );

  const myTasks = data?.my_tasks || {};
  const teamTasks = data?.team_tasks;
  const summary = data?.summary || {};
  const allMyTasks = [
    ...(myTasks.overdue || []),
    ...(myTasks.due_soon || []),
    ...(myTasks.upcoming || []),
    ...(myTasks.no_date || []),
  ];
  const totalMy = summary.my_total || 0;

  const statusLabel = (s) => {
    if (s === 'in_progress') return lang === 'is' ? 'Í vinnslu' : 'In progress';
    if (s === 'done') return lang === 'is' ? 'Lokið' : 'Done';
    if (s === 'todo') return lang === 'is' ? 'Verkefni' : 'To do';
    return lang === 'is' ? 'Opið' : 'Open';
  };

  const priorityLabel = (p) => {
    if (p === 'high') return lang === 'is' ? 'Hár' : 'High';
    if (p === 'low') return lang === 'is' ? 'Lágur' : 'Low';
    return '';
  };

  const renderJob = (job) => {
    const prio = PRIORITY_CONFIG[job.priority] || PRIORITY_CONFIG.normal;
    const PrioIcon = prio.icon;
    return (
      <div
        key={job.job_id}
        className={`card p-3.5 ${job.due_date && job.due_date < new Date().toISOString().slice(0, 10) ? 'border-l-4 border-l-red-500' : prio.border ? `border-l-4 ${prio.border}` : ''}`}
        data-testid={`my-job-${job.job_id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate">{job.title}</p>
              {job.priority && job.priority !== 'normal' && PrioIcon && (
                <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${prio.bg} ${prio.color}`}>
                  <PrioIcon size={10} />
                  {priorityLabel(job.priority)}
                </span>
              )}
            </div>
            <Link to={`/projects/${job.project_id}`} className="text-xs text-accent hover:underline">
              {job.project_name}
            </Link>
            <div className="flex items-center gap-2 mt-1 text-xs text-foreground-muted">
              {job.due_date && (
                <span className="flex items-center gap-1">
                  <Clock size={10} />
                  {formatDate(job.due_date)}
                </span>
              )}
              {job.planned_start_time && job.planned_end_time && (
                <span className="font-mono">{job.planned_start_time} - {job.planned_end_time}</span>
              )}
              {job.assigned_user_names?.length > 0 && (
                <span className="text-foreground-muted/60">{job.assigned_user_names.join(', ')}</span>
              )}
              <span className="text-foreground-muted/40 whitespace-nowrap">{statusLabel(job.status)}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {job.status !== 'done' && (
              <button
                onClick={() => handleStatusChange(job, 'done')}
                className="btn btn-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 text-xs px-2.5 py-1"
                data-testid={`my-job-done-${job.job_id}`}
              >
                {lang === 'is' ? 'Merkja lokið' : 'Mark done'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGroup = (label, tasks, colorClass) => {
    if (!tasks || tasks.length === 0) return null;
    return (
      <div className="space-y-1.5">
        <p className={`text-xs font-medium uppercase tracking-wide ${colorClass}`}>
          {label} ({tasks.length})
        </p>
        {tasks.map(renderJob)}
      </div>
    );
  };

  return (
    <section data-testid="my-jobs-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Briefcase size={16} className="text-accent" />
          <h2 className="text-xs uppercase tracking-wide text-foreground-muted">{lang === 'is' ? 'Mín verk' : 'My tasks'}</h2>
          {totalMy > 0 && (
            <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full font-mono">{totalMy}</span>
          )}
          {summary.my_overdue > 0 && (
            <span className="text-xs bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full font-mono flex items-center gap-1">
              <AlertTriangle size={10} /> {summary.my_overdue}
            </span>
          )}
        </div>
      </div>

      {allMyTasks.length === 0 ? (
        <div className="card p-6 text-center" data-testid="my-jobs-empty">
          <Briefcase className="w-8 h-8 text-foreground-muted/30 mx-auto mb-2" />
          <p className="text-sm text-foreground-muted">{t('dashboard.my_jobs_empty')}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-tasks-list">
          {renderGroup(
            lang === 'is' ? 'Seint' : 'Overdue',
            myTasks.overdue, 'text-red-400'
          )}
          {renderGroup(
            lang === 'is' ? 'Á döfinni' : 'Due soon',
            myTasks.due_soon, 'text-amber-400'
          )}
          {renderGroup(
            lang === 'is' ? 'Framundan' : 'Upcoming',
            myTasks.upcoming, 'text-foreground-muted'
          )}
          {renderGroup(
            lang === 'is' ? 'Engin dagsetning' : 'No date',
            myTasks.no_date, 'text-foreground-muted/60'
          )}
        </div>
      )}

      {/* Team tasks for managers/admins */}
      {isManager && teamTasks && (
        <div className="mt-6" data-testid="team-tasks-section">
          <button
            onClick={() => setShowTeam(!showTeam)}
            className="flex items-center gap-2 w-full text-left mb-3"
            data-testid="team-tasks-toggle"
          >
            <Users size={16} className="text-foreground-muted" />
            <h2 className="text-xs uppercase tracking-wide text-foreground-muted">
              {lang === 'is' ? 'Verk teymisins' : 'Team tasks'}
            </h2>
            {(summary.team_overdue || 0) > 0 && (
              <span className="text-xs bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full font-mono">
                {summary.team_overdue}
              </span>
            )}
            {showTeam ? <ChevronUp size={14} className="text-foreground-muted" /> : <ChevronDown size={14} className="text-foreground-muted" />}
          </button>

          {showTeam && (
            <div className="space-y-3">
              {renderGroup(
                lang === 'is' ? 'Seint' : 'Overdue',
                teamTasks.overdue, 'text-red-400'
              )}
              {renderGroup(
                lang === 'is' ? 'Á döfinni' : 'Due soon',
                teamTasks.due_soon, 'text-amber-400'
              )}
              {renderGroup(
                lang === 'is' ? 'Næstu dagar' : 'Upcoming',
                teamTasks.upcoming, 'text-foreground-muted'
              )}
              {!teamTasks.overdue?.length && !teamTasks.due_soon?.length && !teamTasks.upcoming?.length && (
                <p className="text-sm text-foreground-muted text-center py-4">
                  {lang === 'is' ? 'Engin verkefni teymisins' : 'No team tasks'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {completedJob && (
        <JobCompletionDialog
          job={completedJob}
          onClose={() => { setCompletedJob(null); fetchTasks(); }}
        />
      )}
    </section>
  );
};

export default MyJobsSection;
